import { useNavigation } from '@remix-run/react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import SeparatorLine from '#app/components/common/separator-line'
import { Button } from '#app/components/ui/button'
import {
	Modal,
	ModalTrigger,
	ModalContent,
	ModalHeader,
	ModalTitle,
} from '#app/components/ui/modal'
import { LoadingSpinner } from '#app/components/ui/spinner'
import { useFetcherWithReset } from '#app/hooks/useFetcherWithReset'
import { type action } from '#app/routes/_public+/product+/$styleNumber'
import { cn } from '#app/utils/misc.ts'
import {type PlentyVariant} from "#types/product";
import SizeGuideDialog from './actions/size-guide-dialog'

export interface SizeOption {
	label: string
	value: string
	disabled: boolean
	availableQuantity: number
	storeQuantity?: number
	badgeProps?: {
		type: string
		text: string
	}
	variant?: PlentyVariant
}

interface NewSizeSelectorProps {
	sizeOptions: SizeOption[]
	brandKey: string
	completelySoldOut: boolean
	onAddToBag: (sku: SizeOption) => void
}

export default function NewSizeSelector({
	sizeOptions,
	brandKey,
	completelySoldOut,
	onAddToBag,
}: NewSizeSelectorProps) {
	const [open, setOpen] = useState(false)
	const productFetcher = useFetcherWithReset<typeof action>({ key: 'addToBag' })
	const isFetching = productFetcher.state !== 'idle'
	const [loadingSku, setLoadingSku] = useState<string | null>(null)
	let { t } = useTranslation('product_details_page')
	const navigation = useNavigation()
	const isNavigating = navigation.state !== 'idle'

	// Add effect consoto handle the success case
	React.useEffect(() => {
		if (productFetcher.state === 'idle' && loadingSku) {
			setLoadingSku(null)
			setOpen(false)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [productFetcher.state])

	return (
		<Modal open={open} onOpenChange={setOpen}>
			<ModalTrigger asChild>
				<Button
					className="w-full"
					variant="primary"
					size="primary"
					disabled={completelySoldOut || isNavigating}
				>
					{completelySoldOut ? t('out_of_stock') : t('select_size_label')}
				</Button>
			</ModalTrigger>

			<ModalContent className="max-h-[70vh] w-full p-4" hideCloseButton>
				<ModalHeader className="sticky top-0 flex w-full items-start justify-between bg-white">
					<ModalTitle>{t('select_a_size')}</ModalTitle>
					<div className="flex items-center gap-2">
						<SizeGuideDialog brandKey={brandKey} />
					</div>
				</ModalHeader>

				{isFetching || isNavigating ? (
					<div className="flex w-full justify-center py-8">
						<LoadingSpinner />
					</div>
				) : (
					<div className="mt-4 gap-y-2 flex flex-col overflow-auto">
						{sizeOptions.map(option => (
							<div key={option.value}>
								<div
									className={`flex w-full cursor-pointer justify-between py-3 ${
										option.disabled ? 'opacity-50' : ''
									}`}
									onClick={() => {
										if (!option.disabled && !isFetching && !isNavigating) {
											setLoadingSku(option.value)
											onAddToBag(option)
										}
									}}
								>
									<div className={cn('text-left', option.disabled ? 'line-through' : '')}>{option.label}</div>
									<div
										className={`text-sm text-black-90${
											option.disabled || option.availableQuantity === 0
												? 'text-light-grey-90'
												: option.availableQuantity <= 2 && 'text-red'
										}`}
									>
										{option.badgeProps && (
											<div className="flex flex-row items-center justify-between py-2">
												<div
													className={cn('text-sm',
														option.badgeProps.type === 'availableInStore' ? 'text-accent-foreground' : 'text-red'
													)}
												>
													{option.badgeProps.text}
												</div>
											</div>
										)}
									</div>
								</div>
								<SeparatorLine />
							</div>
						))}
					</div>
				)}
			</ModalContent>
		</Modal>
	)
}
